/**
 * useAdvertTemplate — состояние и логика инициализации рекламы
 */

import { runAdvertInitPipeline } from '../lib/advertInitPipeline'

import type { ShouldSkipPipelineParams, UseAdvertTemplateParams, UseAdvertTemplateResult } from '../types'

import { getInlineStyles } from '../config'

import {
  resolveAdCode,
  buildNodeId,
  buildOptionsSsp,
  buildOptionsPuid,
  checkIsDevMode,
  checkIsAdvDisabled,
  computeShowPlugAds,
  resolveAdScrollLoad,
} from '../helpers'

import { useContext, useEffect, useState } from 'react'

import { useAppSelector } from '@app/model/store/client'
import { GlobalContext } from '@app/context'

import { useCurrentSize } from '@shared/lib/helpers/classes'

const getDynamicCount = (count: unknown): number => parseInt(String(count ?? 0), 10)

const shouldSkipPipeline = (params: ShouldSkipPipelineParams): boolean => {
  const { singleRequest, hasFirstSuccessRequest, skipForSkeletonOrDisabled } = params
  return (singleRequest && hasFirstSuccessRequest) || skipForSkeletonOrDisabled
}

export const useAdvertTemplate = (params: UseAdvertTemplateParams): UseAdvertTemplateResult => {
  const { type, count, singleRequest = false, scrollLoadParam, onEmptyAdv, onLoadedAdv } = params

  const { isMobile, designEnv } = useAppSelector((state) => state.global.client.responsive)

  const storeCurrentPage = useAppSelector((store) => store.global.currentPage)
  const [globalContext] = useContext(GlobalContext)
  const [hasFirstSuccessRequest, setHasFirstSuccessRequest] = useState(false)
  const [typeCurrent] = useCurrentSize(type)
  const [showAdv, setShowAdv] = useState(true)

  const [begunAutoPad, begunBlockId] = resolveAdCode({ typeCurrent, type, isMobile })
  const begunScrollRate = resolveAdScrollLoad({ typeCurrent, type, isMobile, forcedValue: scrollLoadParam })
  const dynamicCount = getDynamicCount(count)
  const nodeId = buildNodeId({ begunAutoPad, begunBlockId, storeCurrentPage, dynamicCount })
  const options_ssp = buildOptionsSsp(begunAutoPad, begunBlockId, begunScrollRate)
  const options_puid = buildOptionsPuid(isMobile)

  const isDev = checkIsDevMode()
  const isSkeletonPage = globalContext.isSkeletonPage
  const isAdvDisabledLocalStorage = checkIsAdvDisabled()
  const skipForSkeletonOrDisabled = isSkeletonPage || isAdvDisabledLocalStorage
  const effectiveShowAdv = skipForSkeletonOrDisabled ? false : showAdv
  const showPlugAds = computeShowPlugAds(effectiveShowAdv, typeCurrent)

  useEffect(() => {
    const isNotReadyPage = designEnv === 'default'
    const skip = shouldSkipPipeline({
      singleRequest,
      hasFirstSuccessRequest,
      skipForSkeletonOrDisabled,
    })

    if (skip || isNotReadyPage) return

    runAdvertInitPipeline({
      setShowAdv,
      onEmptyAdv,
      onLoadedAdv,
      setHasFirstSuccessRequest,
      isDev,
      nodeId,
      options_puid,
      options_ssp,
    })
  }, [globalContext.isSkeletonPage, designEnv === 'default'])

  const inlineStyles = getInlineStyles(typeCurrent) as Record<string, string | null>

  return {
    typeCurrent,
    effectiveShowAdv,
    showPlugAds,
    isDev,
    nodeId,
    dynamicCount,
    inlineStyles,
  }
}
